import "./style.scss";
import { FC, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../../BtnDefault/BtnDefault";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { AOPUkraineRequestData } from "@/src/types/admissionOffice";
import {
  templatesUkraineEnRequestPVCoursesSettings,
  templatesUkraineEnRequestUniversityCoursesSettings,
} from "@/src/constants/AdmissionOffice/Ukraine/AOPUkraineEnConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import CustomSwitch from "@/src/components/CustomSwitch/CustomSwitch";

interface IAOPFormValues {
  whose_doc: string;
  coursePrefix: string;
  currentDate: string;
  documentNumber: string;
  PV: boolean;
  university: boolean;
  fullName: string;
  fullNameEn: string;
  dateOfBirth: string;
  passportNumber: string;
  bodyPV: string;
  secondBody: string;
  withPrint: boolean;
}

interface AOPConfigProps {
  dataForGeneration: AOPUkraineRequestData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPUkraineRequestData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPUkraineRequestData>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ dataForGeneration, setDataForGeneration, setBodyForPrev }) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      bodyPV: "",
      secondBody: "",
      PV: true,
      university: false,
      withPrint: false,
      currentDate: formatDateForInput(today),
    },
  });
  const dispatch = useDispatch();
  const [content, setContent] = useState<string>("");
  const [secondContent, setSecondContent] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const bodyPV = watch("bodyPV");
  const unisersityBody = watch("secondBody");
  const PV = watch("PV");
  const university = watch("university");
  // AUTOCOMPLETE ↓
  const bodyPVAuto = useAutocomplete(Object.keys(templatesUkraineEnRequestPVCoursesSettings));
  const unisersityBodyAuto = useAutocomplete(Object.keys(templatesUkraineEnRequestUniversityCoursesSettings));
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  // EDITORS
  useEffect(() => {
    if (bodyPV) {
      const key = bodyPV as keyof typeof templatesUkraineEnRequestPVCoursesSettings;
      const courseSettings = templatesUkraineEnRequestPVCoursesSettings[key];
      setContent(courseSettings.tinyMCEContent);
      // if (courseSettings) {
      // setValue("selectedCourse", courseSettings.selected_course);
      // setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
      // setValue("duration", courseSettings.duration);
      // setTitle(courseSettings.title);
      // }
    }
  }, [bodyPV, setValue]);

  useEffect(() => {
    if (unisersityBody) {
      const key = unisersityBody as keyof typeof templatesUkraineEnRequestUniversityCoursesSettings;
      const invoiceSettings = templatesUkraineEnRequestUniversityCoursesSettings[key];
      setSecondContent(invoiceSettings ? invoiceSettings.tinyMCEContent : "");
    }
  }, [unisersityBody]);

  const applyTemplateVariables = (
    html: string,
    data: {
      fullName?: string;
      fullNameEn?: string;
      dateOfBirth?: string;
      passportNumber?: string;
    }
  ) => {
    return html
      .replaceAll("[fullName]", data.fullName || "")
      .replaceAll("[fullNameEn]", data.fullNameEn || "")
      .replaceAll("[dateOfBirth]", data.dateOfBirth || "")
      .replaceAll("[passportNumber]", data.passportNumber || "");
  };

  const generateNewDocument = async () => {
    dispatch(setIsPreloyder(true));
    try {
      let response = await admissionOfficeRequests.createUkraineRequestDoc(dataForGeneration);
      if (response) {
        setIsGenerated(true);
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      whose_doc: watch("whose_doc"),
      coursePrefix: watch("coursePrefix"),
      currentDate: watch("currentDate"),
      documentNumber: watch("documentNumber"),
      fullName: watch("fullName"),
      fullNameEn: watch("fullNameEn"),
      dateOfBirth: watch("dateOfBirth"),
      passportNumber: watch("passportNumber"),
      withPrint: watch("withPrint"),
    };

    const replacedPVContent = applyTemplateVariables(content, {
      fullName: watchedFields.fullName,
      fullNameEn: watchedFields.fullNameEn,
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber,
    });

    const replacedUniversityContent = applyTemplateVariables(secondContent, {
      fullName: watchedFields.fullName,
      fullNameEn: watchedFields.fullNameEn,
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber,
    });

    setDataForGeneration((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      fullNameEn: watchedFields.fullNameEn || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber || "",
      withPrint: watchedFields.withPrint,
      bodyPV: PV ? splitHtmlByBlocks(replacedPVContent) : undefined,
      documentNumber: watchedFields.documentNumber || "",
      secondBody: university ? splitHtmlByBlocks(replacedUniversityContent) : undefined,
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      fullNameEn: watchedFields.fullNameEn || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      passportNumber: watchedFields.passportNumber || "",
      withPrint: watchedFields.withPrint,
      bodyPV: PV ? splitHtmlByBlocksWithFontConvert(replacedPVContent) : undefined,
      documentNumber: watchedFields.documentNumber || "",
      secondBody: university ? splitHtmlByBlocksWithFontConvert(replacedUniversityContent) : undefined,
    }));

    const hasContent = content.trim().length > 0 || secondContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      setIsGenerated(false);
    }
  };

  const handlePVEditorChange = (newPVContent: string) => setContent(newPVContent);
  const handleUniversityEditorChange = (newUniversityContent: string) => setSecondContent(newUniversityContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">REQUEST LETTER</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields">
            <div className="form-group-fields-container">
              <div className="form-group-fields-input whose_doc">
                <AutocompleteInput
                  placeholder="Whose"
                  value={whoseDocAuto.query}
                  onChange={whoseDocAuto.onChange}
                  onSelect={(v) => {
                    whoseDocAuto.onSelect(v);
                    setValue("whose_doc", v);
                  }}
                  isOpen={whoseDocAuto.isOpen}
                  options={whoseDocAuto.filtered}
                  error={!!errors.whose_doc}
                  required
                  inputProps={{
                    register: register("whose_doc", { required: true }),
                    close: whoseDocAuto.close,
                    setOpen: whoseDocAuto.onClick,
                  }}
                  identifier={"whose"}
                />
              </div>
              <div className="form-group-fields-input coursePrefix">
                <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              </div>
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
              {errors.currentDate && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="document-number">Document Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="document-number" id="document-number" type="text" {...register("documentNumber")} />
              {errors.documentNumber && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-pv">
              <Controller
                name="PV"
                control={control}
                render={({ field }) => <CustomSwitch label="PV" value={field.value} onChange={field.onChange} />}
              />
            </div>
            <div className="form-group-fields-university">
              <Controller
                name="university"
                control={control}
                render={({ field }) => <CustomSwitch label="University" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name-en">
                  Full Name EN<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame-en" id="full-mame-en" type="text" {...register("fullNameEn", { required: true })} />
              {errors.fullNameEn && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullNameEn")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth", { required: true })} />
              {errors.dateOfBirth && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="passport-number">Passport Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="passport-number" id="passport-number" type="text" {...register("passportNumber")} />
              {errors.passportNumber && <p>This field is required</p>}
            </div>
          </div>
          {PV && (
            <>
              <div className="form-group-fields">
                <div className="form-group-fields-input university-body">
                  <AutocompleteInput
                    label="Select body document PV"
                    value={bodyPVAuto.query}
                    onChange={bodyPVAuto.onChange}
                    onSelect={(v) => {
                      bodyPVAuto.onSelect(v);
                      setValue("bodyPV", v);
                    }}
                    isOpen={bodyPVAuto.isOpen}
                    options={bodyPVAuto.filtered}
                    error={!!errors.bodyPV}
                    required
                    inputProps={{
                      register: register("bodyPV", { required: true }),
                      close: bodyPVAuto.close,
                      setOpen: bodyPVAuto.onClick,
                    }}
                    identifier={"pv"}
                  />
                </div>
              </div>
              <Editor
                value={content}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
                }}
                onEditorChange={handlePVEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}
          {university && (
            <>
              <div className="form-group-fields">
                <div className="form-group-fields-input university-body">
                  <AutocompleteInput
                    label="Select body document university"
                    value={unisersityBodyAuto.query}
                    onChange={unisersityBodyAuto.onChange}
                    onSelect={(v) => {
                      unisersityBodyAuto.onSelect(v);
                      setValue("secondBody", v);
                    }}
                    isOpen={unisersityBodyAuto.isOpen}
                    options={unisersityBodyAuto.filtered}
                    error={!!errors.secondBody}
                    required
                    inputProps={{
                      register: register("secondBody", { required: true }),
                      close: unisersityBodyAuto.close,
                      setOpen: unisersityBodyAuto.onClick,
                    }}
                    identifier={"university"}
                  />
                </div>
              </div>
              <Editor
                value={secondContent}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
                }}
                onEditorChange={handleUniversityEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}
          <div className="form-group-buttons">
            <div className="form-group-buttons-pack">
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
              <BtnDefault disabled={!isGenerated}>
                <p>Download PDF</p>
                <AODSvgExport.DownloadSvg />
              </BtnDefault>
            </div>
            <div className="form-group-fields-print">
              <Controller
                name="withPrint"
                control={control}
                render={({ field }) => <CustomSwitch label="With Print" value={field.value} onChange={field.onChange} />}
              />
            </div>
            <div className="form-group-buttons-refresh" onClick={() => window.location.reload()}>
              <BtnDefault onlyIcon={true}>
                <AODSvgExport.RefreshSvg />
              </BtnDefault>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
